import {Component, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ContactDetailsApiService, ContactDetailsResponse, ReferenceBookType, SubjectApiService} from '@npck/af-frontend-api';
import {LocalizationService, rxSpinner} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import {combineLatest, filter, mergeMap} from 'rxjs';
import {AuthHolderService} from '../../../../../domain/auth-holder.service';
import {DictionaryHolderService, findValueInDictionary} from '../../../../../domain/dictionary-holder.service';
import {formatMsisdnDisplay} from '../../../../../domain/phone-utils';
import {CONTACT_DETAILS_ID, INCIDENT_ID, SUBJECT_ID} from '../../../../../domain/query-params.keys';
import {MainContainerComponent} from '../../../../../layout/main-container/main-container.component';
import {Path} from '../../../../../path';
import {IncidentDetailsTab} from '../../incident-details.component';
import {ContactDetailTab} from './contact/incident-contact.component';

const SUBJECT_CONTEXT_TABS: ContactDetailTab[] = ['tabContactDetails', 'tabFiles'];

@Component({
    selector: 'af-contact-details-page',
    standalone: false,
    templateUrl: './contact-details.component.html',
})
export class ContactDetailsPageComponent {
    @ViewChild('mainContainer') mainContainer: MainContainerComponent;
    Path = Path;
    tabPathToTab: Record<string, IncidentDetailsTab> = {[Path.CONTACT_DETAILS]: 'tabContacts'};
    incidentId: string | null;
    subjectId: string | null;
    contactDetailsId: string;
    contactDetails: ContactDetailsResponse;
    contactTypeName: string;
    allowedTabs?: ContactDetailTab[];

    constructor(
        public authHolderService: AuthHolderService,
        private activatedRoute: ActivatedRoute,
        private contactDetailsApiService: ContactDetailsApiService,
        private subjectApiService: SubjectApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private localizationService: LocalizationService,
        private dialogService: DialogService,
    ) {
        this.incidentId = this.activatedRoute.snapshot.params[INCIDENT_ID] ?? null;
        this.subjectId = this.activatedRoute.snapshot.params[SUBJECT_ID] ?? null;
        this.contactDetailsId = this.activatedRoute.snapshot.params[CONTACT_DETAILS_ID];
        this.allowedTabs = this.subjectId ? SUBJECT_CONTEXT_TABS : undefined;

        const contactDetails$ = this.subjectId
            ? this.subjectApiService.getSubjectContactDetailsById(this.subjectId, this.contactDetailsId)
            : this.contactDetailsApiService.getContactDetailsById(this.incidentId ?? '', this.contactDetailsId);

        combineLatest([
            contactDetails$,
            this.dictionaryHolderService.getReference(ReferenceBookType.CONTACT_TYPE),
        ])
            .pipe(rxSpinner('initial'))
            .subscribe(([response, contactTypes]) => {
                this.contactDetails = response;
                this.contactTypeName = findValueInDictionary(contactTypes, response.contactType, 'name_ru')
                    ?? response.contactType;
            });
    }

    get canDelete(): boolean {
        return !this.subjectId && this.authHolderService.hasAccess(['npck-admin']);
    }

    get title(): string {
        if (!this.contactDetails) {
            return '';
        }
        const msisdn = this.contactDetails.msisdn ? ' ' + formatMsisdnDisplay(this.contactDetails.msisdn) : '';
        return this.contactTypeName + msisdn;
    }

    breadcrumbCustomizer = (path: string): string => {
        if (this.incidentId === path) {
            return this.localizationService.getLocalizedKey('i18n_lib_breadcrumb#IncidentId', {[INCIDENT_ID]: this.incidentId});
        }
        if (this.contactDetailsId === path) {
            return this.title || this.contactDetailsId;
        }
        if (path === Path.CONTACT_DETAILS) {
            return '...';
        }
        return path;
    };

    filter = (path: string): boolean => {
        return path === Path.CONTACT_DETAILS;
    };

    deleteContact(): void {
        if (!this.incidentId) {
            return;
        }
        const incidentId = this.incidentId;
        this.dialogService.openDialog({
            title: 'i18n_af_contactDetailsDeletionDialogTitle',
            subtitle: 'i18n_af_contactDetailsDeletionDialogSubtitle',
            buttonOkText: 'i18n_af_buttonDelete',
            buttonCancelText: 'i18n_af_buttonCancel',
            isButtonOkAccent: true,
            translationParams: {contactType: this.contactTypeName},
        })
            .pipe(
                filter(response => !!response),
                mergeMap(() => this.contactDetailsApiService.deleteContactDetailsById(incidentId, this.contactDetailsId)),
                rxSpinner('delete'),
            )
            .subscribe(() => this.mainContainer.breadcrumbs.navigateBack());
    }
}
